import React from "react";
import cx from "classnames";
import { useController } from "react-hook-form";
import type { Control, FieldPathByValue, FieldValues } from "react-hook-form";
import {
  CloseIcon,
  Collapse,
  OpeningIcon,
  PasswordInput,
  TickIcon,
} from "@picklebet/ui";
import type { PasswordInputProps } from "@picklebet/ui";
import { getPasswordRequirements } from "../models/passwordRules";

import * as styles from "./PasswordRulesField.module.scss";

type PasswordRulesFieldProps<T extends FieldValues> = Omit<
  PasswordInputProps,
  "name" | "value" | "defaultValue" | "onChange" | "onBlur" | "ref"
> & {
  control: Control<T>;
  name: FieldPathByValue<T, string>;
};

const PasswordRulesField = <T extends FieldValues>({
  control,
  name,
  errorMessage,
  hasError,
  ...props
}: PasswordRulesFieldProps<T>) => {
  const { field } = useController({ control, name });
  const value = field.value ?? "";
  const requirements = getPasswordRequirements(value);
  const isInvalid = !!errorMessage || !!hasError;
  const hasUnmetRules =
    value.length > 0 && !requirements.every(({ met }) => met);

  return (
    <div className={styles.container}>
      <PasswordInput
        {...props}
        {...field}
        value={value}
        noSpacing
        hasError={isInvalid}
        errorMessage={hasUnmetRules ? undefined : errorMessage}
      />
      <Collapse isOpen={hasUnmetRules} skipInitial>
        <ul className={styles.list}>
          {requirements.map(({ label, met }) => {
            const Icon = met ? TickIcon : isInvalid ? CloseIcon : OpeningIcon;

            return (
              <li
                key={label}
                className={cx(styles.item, {
                  [styles.met]: met,
                  [styles.unmet]: isInvalid && !met,
                })}
              >
                <Icon
                  className={cx(styles.itemIcon, {
                    [styles.closeIcon]: Icon === CloseIcon,
                  })}
                />
                {label}
              </li>
            );
          })}
        </ul>
      </Collapse>
    </div>
  );
};

export { PasswordRulesField };
